import React, { createContext, useContext, useEffect, useState } from 'react';

type Theme = 'light' | 'dark' | 'system';
const THEME_LS_KEY = 'theme-preference';

interface ThemeContextType {
  theme: Theme;
  effectiveTheme: 'light' | 'dark';
  setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider = ({ children }: { children: React.ReactNode }) => {
  const [theme, setThemeState] = useState<Theme>(() => {
    const storedTheme = typeof window !== 'undefined' ? localStorage.getItem(THEME_LS_KEY) : null;
    return (storedTheme as Theme) || 'system';
  });
  const [effectiveTheme, setEffectiveTheme] = useState<'light' | 'dark'>('light');

  useEffect(() => {
    const root = window.document.documentElement;
    const systemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)');

    const applyTheme = (currentTheme: Theme) => {
      let finalTheme: 'light' | 'dark';
      if (currentTheme === 'system') {
        finalTheme = systemPrefersDark.matches ? 'dark' : 'light';
      } else {
        finalTheme = currentTheme;
      }

      root.classList.remove(finalTheme === 'dark' ? 'light' : 'dark');
      root.classList.add(finalTheme);
      setEffectiveTheme(finalTheme);
      if (typeof window !== 'undefined') {
        localStorage.setItem(THEME_LS_KEY, currentTheme);
      }
    };

    applyTheme(theme);

    const handleChange = (_e: MediaQueryListEvent) => {
      if (theme === 'system') {
        applyTheme('system');
      }
    };

    systemPrefersDark.addEventListener('change', handleChange);
    return () => systemPrefersDark.removeEventListener('change', handleChange);
  }, [theme]);

  const setTheme = (newTheme: Theme) => {
    setThemeState(newTheme);
  };

  return (
    <ThemeContext.Provider value={{ theme, effectiveTheme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
